<template>
  <div class="min-h-screen bg-white z-10">
    <!-- Hero Section -->
    <section class="relative pt-32 pb-20 px-4 overflow-hidden bg-gradient-to-br from-red-50 via-pink-50 to-purple-50">
      <div class="absolute inset-0">
        <div class="absolute top-20 left-10 w-96 h-96 bg-red-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
        <div class="absolute bottom-20 right-10 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
      </div>
      
      <div class="relative max-w-4xl mx-auto text-center">
        <h1 class="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-red-600 to-pink-600">
          We're building the future of content creation
        </h1>
        <p class="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
          At Peachy Pro Studios, we believe every creator deserves professional-grade tools and talent to bring their vision to life—without the complexity or cost of traditional production.
        </p>
      </div>
    </section>

    <!-- Mission Section -->
    <section class="py-24 px-4">
      <div class="max-w-6xl mx-auto">
        <div class="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <h2 class="text-4xl font-bold mb-6 text-gray-900">Our Mission</h2>
            <div class="space-y-6 text-lg text-gray-600 leading-relaxed">
              <p>
                Content creation shouldn't be complicated. Yet for years, creators and teams have struggled with disconnected tools, expensive software, and the challenge of finding reliable editing talent.
              </p>
              <p>
                We built Peachy Pro Studios to change that. Our platform brings together everything you need—from initial concept to final distribution—in one seamless workflow.
              </p>
              <p>
                Whether you're a solo content creator crafting your next viral video, a marketing agency managing multiple clients, or an enterprise team coordinating global campaigns, we've created a space where creativity flows naturally and collaboration happens effortlessly.
              </p>
            </div>
          </div>
          
          <div class="relative">
            <div class="bg-gradient-to-br from-red-100 to-pink-100 rounded-2xl p-8 transform hover:scale-105 transition-transform duration-300">
              <div class="space-y-6">
                <div class="flex items-start space-x-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 class="font-semibold text-gray-900 mb-2">Simplify Creation</h3>
                    <p class="text-gray-600">One platform for planning, producing, and publishing content.</p>
                  </div>
                </div>
                
                <div class="flex items-start space-x-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 class="font-semibold text-gray-900 mb-2">Connect Talent</h3>
                    <p class="text-gray-600">Access vetted editors who understand your creative vision.</p>
                  </div>
                </div>
                
                <div class="flex items-start space-x-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl flex items-center justify-center">
                    <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 class="font-semibold text-gray-900 mb-2">Deliver Excellence</h3>
                    <p class="text-gray-600">Professional results, every time, without the premium price tag.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Values Section -->
    <section class="py-24 px-4 bg-gray-50">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-4 text-gray-900">Our Values</h2>
          <p class="text-xl text-gray-600">The principles that guide everything we do</p>
        </div>
        
        <div class="grid md:grid-cols-3 gap-8">
          <div class="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-300">
            <div class="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-bold mb-4 text-gray-900">Creator-First</h3>
            <p class="text-gray-600 leading-relaxed">
              Every decision we make starts with one question: "Does this make content creation easier for our users?" We're obsessed with removing friction from the creative process.
            </p>
          </div>
          
          <div class="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-300">
            <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-bold mb-4 text-gray-900">Quality & Trust</h3>
            <p class="text-gray-600 leading-relaxed">
              We vet every editor, secure every file, and ensure every project meets our high standards. Your content and your brand are safe with us.
            </p>
          </div>
          
          <div class="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-shadow duration-300">
            <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-bold mb-4 text-gray-900">Innovation</h3>
            <p class="text-gray-600 leading-relaxed">
              The content landscape evolves daily. We stay ahead of trends, embrace new technologies, and continuously improve our platform to keep you competitive.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Team Section -->
    <section class="py-24 px-4">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-4 text-gray-900">Meet Our Team</h2>
          <p class="text-xl text-gray-600">The creative minds behind Peachy Pro Studios</p>
        </div>
        
        <div class="grid md:grid-cols-3 gap-12">
          <!-- Team Member 1 -->
          <div class="group text-center">
            <div class="relative mb-6 overflow-hidden rounded-2xl">
              <div class="aspect-square bg-gradient-to-br from-red-200 to-pink-300 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                <svg class="w-24 h-24 text-white opacity-50" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path>
                </svg>
              </div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">Shahzeb Soomro</h3>
            <p class="text-red-600 font-medium mb-3">Co-Founder</p>
            <p class="text-gray-600 leading-relaxed">
              Wahid's vision drives our mission to democratize professional video production.
            </p>
          </div>
          
          <!-- Team Member 2 -->
          <div class="group text-center">
            <div class="relative mb-6 overflow-hidden rounded-2xl">
              <div class="aspect-square bg-gradient-to-br from-purple-200 to-blue-300 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                <svg class="w-24 h-24 text-white opacity-50" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path>
                </svg>
              </div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">Farid Siddiqi</h3>
            <p class="text-purple-600 font-medium mb-3">Co-Founder</p>
            <p class="text-gray-600 leading-relaxed">
              Fintech engineer passionate about building scalable platforms. Farid architected the technology that makes seamless collaboration possible.
            </p>
          </div>
          
          <!-- Team Member 3 -->
          <div class="group text-center">
            <div class="relative mb-6 overflow-hidden rounded-2xl">
              <div class="aspect-square bg-gradient-to-br from-green-200 to-teal-300 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                <svg class="w-24 h-24 text-white opacity-50" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path>
                </svg>
              </div>
              <div class="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-2">Julian</h3>
            <p class="text-green-600 font-medium mb-3">Editor</p>
            <p class="text-gray-600 leading-relaxed">
              Amazing editor and creator advocate. Julian ensures every project on our platform exceeds expectations.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="py-24 px-4 bg-gradient-to-br from-red-500 via-pink-600 to-purple-600 relative overflow-hidden">
      <div class="absolute inset-0">
        <div class="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
        <div class="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
      </div>
      
      <div class="relative max-w-6xl mx-auto">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold mb-4 text-white">Our Impact</h2>
          <p class="text-xl text-white/90">Growing together with our creative community</p>
        </div>
        
        <div class="grid md:grid-cols-4 gap-8">
          <div class="text-center">
            <div class="text-5xl font-bold text-white mb-2">100+</div>
            <div class="text-white/90 text-lg">Projects</div>
          </div>
          <div class="text-center">
            <div class="text-5xl font-bold text-white mb-2">Global</div>
            <div class="text-white/90 text-lg">Vetted Editors</div>
          </div>
          <div class="text-center">
            <div class="text-5xl font-bold text-white mb-2">3+</div>
            <div class="text-white/90 text-lg">Countries Served</div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-24 px-4">
      <div class="max-w-4xl mx-auto text-center">
        <h2 class="text-4xl md:text-5xl font-bold mb-6 text-gray-900">Join Our Mission</h2>
        <p class="text-xl text-gray-600 mb-10 leading-relaxed">
          Whether you're a creator looking for the perfect editor, a talented editor seeking meaningful projects, or simply someone who believes in making content creation accessible—we'd love to hear from you.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <NuxtLink to="/request-demo"
            class="bg-gradient-to-r from-red-500 to-pink-600 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:shadow-xl hover:scale-105 transition-all duration-200">
            Get Started Today
          </NuxtLink>
          <NuxtLink to="/auth/login"
            class="bg-white text-gray-700 border-2 border-gray-300 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-all duration-200">
            Join as an Editor
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
definePageMeta({
});
</script>

<style scoped>
@keyframes blob {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}
</style>